import {Component, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';

import {debounceTime, switchMap, takeUntil} from 'rxjs/operators';
import {interval, Subject} from 'rxjs';

import {MonitorInfoComponent} from './monitor-info/monitor-info.component';
import {Router} from '@angular/router';
import {HttpParams} from '@angular/common/http';
import {GroupTypeService, MessagesService, SnackbarService} from 'src/app/services';
import {MessageState} from 'src/app/models';

@Component({
    selector: 'app-monitor-ui',
    templateUrl: './monitor-ui.component.html',
    styleUrls: ['./monitor-ui.component.scss']
})
export class MonitorUIComponent implements OnInit {
    displayAdvancedSearch = false;
    fromDateControl: UntypedFormControl;
    toDateControl: UntypedFormControl;
    senderControl: UntypedFormControl;
    messageContentControl: UntypedFormControl;
    recipientControl: UntypedFormControl;
    groupTypeControl: UntypedFormControl;
    acknowledgementStateControl: UntypedFormControl;
    showMessagesNOTFromSVCControl: UntypedFormControl;
    toDate: any;
    fromDate: any;
    from: any;
    to: any;
    groupType: any;
    groupTypes: any;
    acknowledgementState: any;
    showMessagesNOTFromSVC: boolean;
    messageText: any;
    dbColumn: string;
    advancedSearch = false;
    hourglass = false;
    buttonsDisabled = false;
    messageStates = [
        MessageState.ALL,
        MessageState.ALL_WITHOUT_ACKNOWLEDGEMENT,
        MessageState.ALL_WITH_ACKNOWLEDGEMENT,
        MessageState.IN_PROGRESS_ESCALATED_NEXT_TIER,
        MessageState.COMPLETED_UNRESPONSIVE
    ];
    // Sent Alerts Datatable
    alerts: any[];
    selectedAlert = [];

    page = {
        limit: 20,
        count: 0,
        offset: 0,
        orderBy: 'createdTime',
        orderDir: 'desc'
    };
    alertColumns = [
        {name: 'Ack.'},
        {name: 'From'},
        {name: 'To'},
        {name: 'Message'},
        {name: 'Sent'}
    ];
    dateWarning = true;
    params = new HttpParams();
    private updateAdvancedSearch = new Subject<string>();

    constructor(private dialog: MatDialog,
                public datePipe: DatePipe,
                private snackbarService: SnackbarService,
                public messagesService: MessagesService,
                public groupTypeService: GroupTypeService,
                public router: Router) {
        this.fromDateControl = new UntypedFormControl(new Date());
        this.fromDateControl.value.setDate(this.fromDateControl.value.getDate() - 8); // display date from 8 days ago by default
        this.toDateControl = new UntypedFormControl(new Date());
        this.messageContentControl = new UntypedFormControl('');
        this.senderControl = new UntypedFormControl('');
        this.recipientControl = new UntypedFormControl('');
        this.groupTypeControl = new UntypedFormControl('');
        this.acknowledgementStateControl = new UntypedFormControl('');
        this.acknowledgementStateControl.setValue(MessageState.ALL);
        this.showMessagesNOTFromSVCControl = new UntypedFormControl('');
    }

    ngOnInit() {
        this.groupTypeService.getGroupTypes().subscribe(gTypes => this.groupTypes = gTypes);
        this.pageCallback({offset: 0});
        const fiveMinuteInterval = interval(1000 * 60 * 5);
        fiveMinuteInterval.pipe(takeUntil(this.router.events)).subscribe(() => this.pageCallback({offset: 0}));
        this.updateAdvancedSearch.pipe(
            debounceTime(1000), // wait 200 milliseconds after each keystroke to not spam requests
            switchMap(offset => this.messagesService.getMessagesFilter(this.page, offset, this.fromDate, this.toDate, this.messageText, this.from, this.to, this.groupType, this.acknowledgementState, this.showMessagesNOTFromSVC))
        ).subscribe({
            next: (resData: any[]) => {
                this.page.count = resData['total_elements'];
                this.alerts = resData['content'];
                this.advancedSearch = true;
                this.displayProcessing(false);
            }, error: () => {
                this.snackbarService.openSnackBar('Error displaying results, please check fields and try again', true);
                this.displayProcessing(false);
            }
        });
    }

    pageChanged(event) {
        if (!this.hourglass) {
            this.pageCallback(event);
        }
    }

    pageCallback(pageInfo: { count?: number, pageSize?: number, limit?: number, offset?: number }) {
        this.page.offset = pageInfo.offset;
        (this.advancedSearch === false) ? this.performBasicSearch() : this.performAdvancedSearch(pageInfo.offset);
    }

    sortChanged(event) {
        if (!this.hourglass) {
            this.sortCallback(event);
        }
    }

    sortCallback(sortInfo: { sorts: { dir: string, prop: string }[], column: {}, prevValue: string, newValue: string }) {
        this.page.orderDir = sortInfo.sorts[0].dir;
        this.page.orderBy = this.getDatabaseField(sortInfo.sorts[0].prop);

        (this.advancedSearch === false) ? this.performBasicSearch() : this.performAdvancedSearch(this.page.offset || 0);
    }

    performBasicSearch() {
        this.displayProcessing(true);
        this.dbColumn = this.getDatabaseField(this.page.orderBy);
        this.params = new HttpParams();
        this.params = this.params.set('sort', `${this.page.orderBy},${this.page.orderDir}`)
            .set('page', `${this.page.offset}`)
            .set('size', `${this.page.limit}`);
        this.toDate = (this.toDateControl.value === '' || null) ? '' : this.datePipe.transform(this.convertLocalToUtc(this.toDateControl.value), 'MM/dd/yyyy HH:mm:ss');
        this.fromDate = (this.fromDateControl.value === '' || null) ? '' : this.datePipe.transform(this.convertLocalToUtc(this.fromDateControl.value), 'MM/dd/yyyy HH:mm:ss');
        this.messagesService.getMessages(this.fromDate, this.toDate, this.params).subscribe({
            next: (resData: any[]) => {
                this.page.count = resData['total_elements'];
                this.alerts = resData['content'];
                this.alerts.forEach(alert => {
                    alert.from = alert.sent_by == undefined ? alert.from : alert.sent_by;
                });
                this.advancedSearch = false;
            }
        }).add(() => this.displayProcessing(false));
    }

    isError(alert: any) {
        return (this.isWebhookError(alert) || this.isTwilioError(alert));
    }

    isWebhookError(alert: any) {
        for (const recipient of alert.recipients) {
            for (const status of recipient.statuses) {
                if(status.status.includes('Error sending webhook message')) {
                    return true;
                }
            }
        }
        return false;
    }

    isTwilioError(alert: any) {
        for (const recipient of alert.recipients) {
            for (const status of recipient.statuses) {
                if(status.status.includes('Error sending sms message')) {
                    return true;
                }
            }
        }
        return false;
    }

    performAdvancedSearch(offset: any) {
        this.displayProcessing(true);
        if (((this.toDateControl.value === '' || null) && (this.fromDateControl.value !== '' || null)) || ((this.fromDateControl.value === '' || null) && (this.toDateControl.value !== '' || null))) {
            this.snackbarService.openSnackBar('Please enter either a start AND end date or neither', true);
            this.displayProcessing(false);
            return;
        }

        this.toDate = (this.toDateControl.value === '' || null) ? '' : this.datePipe.transform(this.convertLocalToUtc(this.toDateControl.value), 'MM/dd/yyyy HH:mm:ss');
        this.fromDate = (this.fromDateControl.value === '' || null) ? '' : this.datePipe.transform(this.convertLocalToUtc(this.fromDateControl.value), 'MM/dd/yyyy HH:mm:ss');

        if (new Date(this.toDate) < new Date(this.fromDate)) {
            this.snackbarService.openSnackBar('Please enter a start date before the end date.', true);
            this.displayProcessing(false);
            return;
        }

        this.dbColumn = this.getDatabaseField(this.page.orderBy);
        this.params = new HttpParams();
        this.params = this.params.set('sort', `${this.getDatabaseField(this.page.orderBy)},${this.page.orderDir}`)
            .set('page', `${this.page.offset}`)
            .set('size', `${this.page.limit}`);
        // NOTE: those params key values depends on your API!
        this.messageText = escape(this.messageContentControl.value);
        this.from = escape(this.senderControl.value);
        this.to = escape(this.recipientControl.value);
        this.groupType = this.groupTypeControl.value ? escape(this.groupTypeControl.value) : ''; // Prevents undefined From Being Passed To The API
        this.acknowledgementState = escape(this.acknowledgementStateControl.value);
        this.showMessagesNOTFromSVC = this.showMessagesNOTFromSVCControl.value;
        if (!this.fromDate && !this.toDate && !this.messageText && !this.from && !this.to && !this.groupType && !this.acknowledgementState && !this.showMessagesNOTFromSVC) {
            this.performBasicSearch();
        } else {
            this.page.offset = offset;
            this.updateAdvancedSearch.next(offset);
        }
    }

    convertLocalToUtc(localDate : Date) : Date {
        let utcDate = new Date(localDate);
        utcDate.setHours(0, 0, 0, 0);
        let localOffset = utcDate.getTimezoneOffset() * 60000;
        let utcTime = utcDate.getTime() + localOffset;

        utcDate.setTime(utcTime);

        return utcDate;
    }

    performBasicSearchBySender(offset: any) {
        if (!this.displayAdvancedSearch && (this.senderControl.value === '' || this.senderControl.value == undefined)) {
            this.performBasicSearch();
        }
        if (!this.displayAdvancedSearch && this.senderControl.value != undefined && escape(this.senderControl.value).trim().length > 2) {
            this.performAdvancedSearch(offset);
        }
    }

    displayProcessing(toggle: boolean) {
        this.buttonsDisabled = toggle;
        this.hourglass = toggle;
    }

    /**
     * Convert to column names to expected model field names
     */
    getDatabaseField(orderBy) {
        switch (orderBy.toUpperCase()) {
            case('FROM'):
                return 'createdBy';
            case('SENT'):
                return 'createdTime';
            default:
                return 'createdTime';
        }
    }

    /**
     * A little brute force to dynamically set datatable widths.
     */
    getShowScrollbar(datatable: any) {
        return datatable.element.offsetWidth <= 900;
    }

    getRowColor(row: any) {
        const now = +new Date();
        const sent = +new Date(row.sent);
        const MILLI_MIN = 60 * 1000;
        const FIVE_MIN = 5 * MILLI_MIN;
        const TEN_MIN = 10 * MILLI_MIN;
        const elapsed = now - sent;

        return {
            'green': (row.responseRequired && row.acceptedCount > 0),
            'yellow': row.responseRequired && !row.acceptedCount && (elapsed < FIVE_MIN),
            'orange': row.responseRequired && !row.acceptedCount && (elapsed >= FIVE_MIN && elapsed < TEN_MIN),
            'red': (row.declinedCount > 0 && !row.acceptedCount) || (row.responseRequired && !row.acceptedCount && (elapsed >= TEN_MIN))
        };
    }

    // search data function to and from date and message content

    resetAdvancedSearchCriteria() {
        this.senderControl.setValue('');
        this.toDateControl.setValue(new Date());
        this.fromDateControl.setValue(new Date(new Date().setDate(new Date().getDate() - 8)));
        this.recipientControl.setValue('');
        this.groupTypeControl.setValue('');
        this.acknowledgementStateControl.setValue('');
        this.showMessagesNOTFromSVCControl.setValue('');
        this.messageContentControl.setValue('');
        this.performBasicSearch();
    }

    clearDateField(fieldNum: number) {
        if (fieldNum === 1) {
            this.fromDateControl.setValue('');
        } else {
            this.toDateControl.setValue('');
        }
    }

    initializeAdvancedSearch() {
        this.displayAdvancedSearch = true;
    }

    initializeBasicSearch() {
        this.resetAdvancedSearchCriteria();
        this.displayAdvancedSearch = false;
        this.advancedSearch = false;
    }

    // onclick of row function
    openAlertDetail(event: any) {
        if (event.type === 'click') {
            this.selectedAlert = [];
            const alert = event.row;
            this.dialog.open(MonitorInfoComponent, {
                width: '90%',
                height: '90%',
                panelClass: 'alert-info-dialog',
                data: {
                    currentAlert: alert
                }
            });
        }
    }

    getTypeCase(s: string) {
        return s.split('_').map(w => w[0].toUpperCase() + w.substring(1).toLowerCase()).join(' ');
    }

    //Returns a string with Unicode character codes (e.g. &#65; for "A") parsed into characters
    unicodeToText(text: string) {
        const oldText = document.createElement('DIV');
        oldText.innerHTML = text;
        return oldText.textContent || oldText.innerText || '';
    }
}
